import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Users, CalendarDays, Package, ShoppingCart, Inbox, Mail, Megaphone, Loader2 } from "lucide-react";
import { ElephantLogo } from "@/components/ElephantLogo";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const TABS = [
  { key: "stats", label: "Aperçu", icon: Package },
  { key: "artists", label: "Artistes", icon: Users },
  { key: "events", label: "Événements & Tournées", icon: CalendarDays },
  { key: "products", label: "Produits", icon: Package },
  { key: "orders", label: "Commandes", icon: ShoppingCart },
  { key: "bookings", label: "Demandes booking", icon: Inbox },
  { key: "services", label: "Demandes services", icon: Megaphone },
  { key: "messages", label: "Messages", icon: Mail },
];

const FIELDS = {
  artists: [
    { k: "name", label: "Nom" }, { k: "genre", label: "Style musical (FR)" }, { k: "genre_en", label: "Style musical (EN)" },
    { k: "image", label: "Image de couverture (URL)" }, { k: "image_position", label: "Position de l'image (ex : 50% 30%)" },
    { k: "photos_txt", label: "Galerie : une photo par ligne, « URL | Crédit »", ta: true },
    { k: "bio_fr", label: "Bio (FR)", ta: true },
    { k: "bio_en", label: "Bio (EN)", ta: true },
    { k: "origin", label: "Origine (FR, ex : Guinée · France)" }, { k: "origin_en", label: "Origine (EN)" }, { k: "epk", label: "Lien EPK" },
    { k: "sm_instagram", label: "Instagram (URL)" }, { k: "sm_tiktok", label: "TikTok (URL)" },
    { k: "sm_facebook", label: "Facebook (URL)" }, { k: "sm_youtube", label: "YouTube (URL)" },
    { k: "sm_spotify", label: "Spotify (URL)" },
    { k: "videos_txt", label: "Vidéos YouTube (une URL par ligne)", ta: true },
  ],
  events: [
    { k: "title", label: "Titre (FR)" }, { k: "title_en", label: "Titre (EN)" }, { k: "artist", label: "Artiste (FR)" }, { k: "artist_en", label: "Artiste (EN)" },
    { k: "city", label: "Ville (FR)" }, { k: "city_en", label: "Ville (EN)" },
    { k: "venue", label: "Salle (FR)" }, { k: "venue_en", label: "Salle (EN)" }, { k: "date", label: "Date (YYYY-MM-DD)" }, { k: "ticket_url", label: "Lien billetterie" },
    { k: "image", label: "Image (URL)" }, { k: "status", label: "Statut (upcoming/past)" }, { k: "is_tour", label: "Sily Tour (true/false)" },
  ],
  products: [
    { k: "name", label: "Nom (FR)" }, { k: "name_en", label: "Nom (EN)" }, { k: "category", label: "Catégorie (artisanat/mode/culturel/merch)" },
    { k: "price", label: "Prix (€)", num: true }, { k: "image", label: "Image (URL)" },
    { k: "description", label: "Description (FR)", ta: true }, { k: "description_en", label: "Description (EN)", ta: true }, { k: "stock", label: "Stock", num: true },
  ],
};

const SM = ["instagram", "tiktok", "facebook", "youtube", "spotify"];
const toArtistForm = (row) => {
  const f = { ...row, videos_txt: (row.videos || []).join("\n"),
    photos_txt: (row.photos || []).map((p) => (row.photo_credits?.[p] ? `${p} | ${row.photo_credits[p]}` : p)).join("\n") };
  SM.forEach((k) => { f[`sm_${k}`] = row.socials?.[k] || ""; });
  return f;
};
const fromArtistForm = (p) => {
  const socials = { ...(p.socials || {}) };
  SM.forEach((k) => { socials[k] = (p[`sm_${k}`] || "").trim(); delete p[`sm_${k}`]; });
  p.socials = socials;
  p.videos = (p.videos_txt || "").split("\n").map((s) => s.trim()).filter(Boolean);
  delete p.videos_txt;
  const lines = (p.photos_txt || "").split("\n").map((s) => s.trim()).filter(Boolean);
  p.photos = lines.map((l) => l.split("|")[0].trim());
  p.photo_credits = Object.fromEntries(lines.filter((l) => l.includes("|")).map((l) => [l.split("|")[0].trim(), l.split("|").slice(1).join("|").trim()]));
  delete p.photos_txt;
  return p;
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [data, setData] = useState({});
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  const endpoint = { orders: "orders", bookings: "booking-requests", services: "service-requests", messages: "contact-messages" };

  const load = useCallback(async (t) => {
    try {
      if (t === "stats") { const { data } = await api.get("/admin/stats"); setStats(data); return; }
      const path = endpoint[t] || t;
      const { data: rows } = await api.get(`/admin/${path}`);
      setData((d) => ({ ...d, [t]: rows }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user?.role === "admin") load(tab); }, [tab, user, load]);

  const openCreate = () => { setEditing(null); setForm(tab === "artists" ? toArtistForm({}) : {}); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm(tab === "artists" ? toArtistForm(row) : row); setDialogOpen(true); };

  const save = async () => {
    setBusy(true);
    const payload = { ...form };
    if (tab === "artists") fromArtistForm(payload);
    FIELDS[tab].forEach((f) => {
      if (f.num) payload[f.k] = parseFloat(payload[f.k] || 0);
      if (f.k === "is_tour") payload.is_tour = String(payload.is_tour) === "true";
    });
    try {
      if (editing) await api.put(`/admin/${tab}/${editing.id}`, payload);
      else await api.post(`/admin/${tab}`, payload);
      toast.success("Enregistré");
      setDialogOpen(false);
      load(tab);
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try { await api.delete(`/admin/${tab}/${id}`); toast.success("Supprimé"); load(tab); }
    catch { toast.error("Erreur"); }
  };

  const updateBookingStatus = async (id, status) => {
    try { await api.patch(`/admin/booking-requests/${id}`, { status }); load("bookings"); } catch {}
  };

  const updateServiceStatus = async (id, status) => {
    try { await api.patch(`/admin/service-requests/${id}`, { status }); load("services"); } catch {}
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-obsidian"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  const rows = data[tab] || [];

  return (
    <div className="min-h-screen bg-obsidian flex flex-col lg:flex-row">
      {/* sidebar */}
      <aside className="lg:w-64 border-b lg:border-b-0 lg:border-r border-gold/15 p-5 lg:p-6 lg:sticky lg:top-0 lg:h-screen flex lg:flex-col gap-2 overflow-x-auto">
        <div className="flex items-center gap-2 mb-6 shrink-0">
          <ElephantLogo className="w-8 h-8" />
          <span className="font-serif text-xl">Sily Admin</span>
        </div>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === t.key ? "bg-gold text-obsidian" : "text-cream/70 hover:bg-white/5"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
        <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-crimson-accent hover:bg-crimson-accent/10 lg:mt-auto whitespace-nowrap">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </aside>

      {/* content */}
      <main className="flex-1 p-5 lg:p-10 overflow-x-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl lg:text-4xl">{TABS.find((t) => t.key === tab)?.label}</h1>
          {FIELDS[tab] && (
            <button onClick={openCreate} data-testid="admin-add-btn" className="btn-gold rounded-full px-5 py-2.5 flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          )}
        </div>

        {tab === "stats" && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats">
            {[["Artistes", stats.artists], ["Événements", stats.events], ["Produits", stats.products], ["Commandes", stats.orders], ["Demandes", stats.bookings], ["Services", stats.services], ["Messages", stats.messages], ["Revenu (€)", stats.revenue]].map(([l, v]) => (
              <div key={l} className="card-dark rounded-xl p-6">
                <p className="text-xs uppercase tracking-widest text-cream/50">{l}</p>
                <p className="font-serif text-4xl text-gold mt-2">{v ?? 0}</p>
              </div>
            ))}
          </div>
        )}

        {FIELDS[tab] && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`admin-list-${tab}`}>
            {rows.map((row) => (
              <div key={row.id} className="card-dark rounded-xl p-5" data-testid={`admin-item-${row.id}`}>
                {row.image && <img src={row.image} alt="" className="w-full h-32 object-cover rounded mb-3" />}
                <p className="font-serif text-lg">{row.name || row.title}</p>
                <p className="text-sm text-cream/50">{row.genre || row.category || `${row.artist || ""} ${row.city || ""}`}</p>
                {row.price != null && <p className="text-gold mt-1">{Number(row.price).toFixed(2)} €</p>}
                {row.date && <p className="text-xs text-cream/40 mt-1">{row.date}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => openEdit(row)} data-testid={`admin-edit-${row.id}`} className="flex-1 btn-outline-gold rounded py-2 text-xs flex items-center justify-center gap-1"><Pencil className="w-3 h-3" /> Modifier</button>
                  <button onClick={() => del(row.id)} data-testid={`admin-delete-${row.id}`} className="px-3 rounded border border-crimson-accent/40 text-crimson-accent hover:bg-crimson-accent/10"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {rows.length === 0 && <p className="text-cream/40 col-span-full py-10 text-center">Aucun élément.</p>}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="admin-orders-list">
            {rows.map((o) => (
              <div key={o.id} className="card-dark rounded-xl p-5 flex flex-wrap justify-between gap-3">
                <div>
                  <p className="font-medium">{o.customer_name || "—"} · <span className="text-cream/50">{o.customer_email}</span></p>
                  <p className="text-sm text-cream/50">{o.items?.map((i) => `${i.name} x${i.quantity}`).join(", ")}</p>
                  <p className="text-xs text-cream/40 mt-1">{o.created_at?.slice(0, 16).replace("T", " ")}</p>
                </div>
                <div className="text-right">
                  <p className="text-gold text-lg">{Number(o.total).toFixed(2)} €</p>
                  <span className={`text-xs px-2 py-0.5 rounded border ${o.payment_status === "paid" ? "text-emerald-accent border-emerald-accent/40 bg-emerald-accent/10" : "text-cream/50 border-white/10"}`}>{o.payment_status}</span>
                </div>
              </div>
            ))}
            {rows.length === 0 && <p className="text-cream/40 py-10 text-center">Aucune commande.</p>}
          </div>
        )}

        {tab === "bookings" && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            {rows.map((b) => (
              <div key={b.id} className="card-dark rounded-xl p-5" data-testid={`admin-booking-${b.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-medium">{b.name} {b.organization && <span className="text-cream/50">· {b.organization}</span>}</p>
                    <p className="text-sm text-cream/60">{b.email} · {b.phone}</p>
                    <p className="text-sm text-gold mt-1">{b.artist} · {b.event_type} · {b.city} · {b.date}</p>
                    <p className="text-sm text-cream/50">Budget: {b.budget}</p>
                    {b.message && <p className="text-sm text-cream/60 mt-2 italic">"{b.message}"</p>}
                  </div>
                  <select value={b.status} onChange={(e) => updateBookingStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`}
                    className="h-9 bg-white/5 border border-gold/20 rounded px-2 text-sm outline-none self-start">
                    <option value="new">Nouveau</option>
                    <option value="in_progress">En cours</option>
                    <option value="confirmed">Confirmé</option>
                    <option value="declined">Refusé</option>
                  </select>
                </div>
              </div>
            ))}
            {rows.length === 0 && <p className="text-cream/40 py-10 text-center">Aucune demande.</p>}
          </div>
        )}

        {tab === "services" && (
          <div className="space-y-3" data-testid="admin-services-list">
            {rows.map((s) => (
              <div key={s.id} className="card-dark rounded-xl p-5" data-testid={`admin-service-${s.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-sm text-cream/60">{s.email} · {s.phone}</p>
                    <p className="text-sm text-gold mt-1">{s.service}{s.plan ? ` · ${s.plan}` : ""}{s.budget ? ` · ${s.budget}` : ""}</p>
                    {s.message && <p className="text-sm text-cream/60 mt-2 italic">"{s.message}"</p>}
                    <p className="text-xs text-cream/40 mt-2">{s.created_at?.slice(0, 16).replace("T", " ")}</p>
                  </div>
                  <select value={s.status} onChange={(e) => updateServiceStatus(s.id, e.target.value)} data-testid={`service-status-${s.id}`}
                    className="h-9 bg-white/5 border border-gold/20 rounded px-2 text-sm outline-none self-start">
                    <option value="new">Nouveau</option>
                    <option value="in_progress">En cours</option>
                    <option value="confirmed">Confirmé</option>
                    <option value="declined">Refusé</option>
                  </select>
                </div>
              </div>
            ))}
            {rows.length === 0 && <p className="text-cream/40 py-10 text-center">Aucune demande.</p>}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages-list">
            {rows.map((m) => (
              <div key={m.id} className="card-dark rounded-xl p-5">
                <p className="font-medium">{m.name} · <span className="text-cream/50">{m.email}</span></p>
                <p className="text-gold text-sm">{m.subject}</p>
                <p className="text-sm text-cream/60 mt-2">{m.message}</p>
                <p className="text-xs text-cream/40 mt-2">{m.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
            {rows.length === 0 && <p className="text-cream/40 py-10 text-center">Aucun message.</p>}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121215] border border-gold/25 text-cream max-h-[85vh] overflow-y-auto" data-testid="admin-form-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Modifier" : "Ajouter"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            {(FIELDS[tab] || []).map((f) => (
              <div key={f.k}>
                <label className="text-xs uppercase tracking-widest text-cream/50 mb-1.5 block">{f.label}</label>
                {f.ta ? (
                  <textarea rows={3} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`form-field-${f.k}`}
                    className="w-full bg-white/5 border border-gold/20 rounded px-3 py-2.5 text-sm outline-none focus:border-gold resize-none" />
                ) : (
                  <input value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`form-field-${f.k}`}
                    className="w-full bg-white/5 border border-gold/20 rounded px-3 py-2.5 text-sm outline-none focus:border-gold" />
                )}
              </div>
            ))}
            <button onClick={save} disabled={busy} data-testid="admin-save-btn" className="btn-gold rounded-full w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
